import {
  Button,
  Checkbox,
  Combobox,
  DatePicker,
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  ModuleWrapper,
  MonthCombobox,
  NumberInput,
  WeekdayToggleGroup,
} from '@/components'
import {
  bitmaskToWeekdays,
  useBreadcrumbOverrides,
  useFormWatch,
  useParam,
  weekdaysToBitmask,
} from '@/utils'
import { dateFormat } from '@/utils/graphql'
import { skipToken, useMutation, useSuspenseQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { parseISO } from 'date-fns'
import type { VariablesOf } from 'gql.tada'
import { Activity, Suspense, useState } from 'react'
import type { FieldErrors } from 'react-hook-form'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import z from 'zod'
import { UPSERT_ABSENCE_LOCK_CONFIG_MUTATION } from '../mutations'
import { ABSENCE_LOCK_CONFIG_QUERY } from '../queries'
import { buildRRule, parseRRuleParts, RRuleWeekdayByIndex } from '../util'
import { AbsenceLockUpcoming } from './absence-lock-upcoming'
import { LockActionsToggle } from './lock-actions-toggle'

const WEEKDAY_BY_INDEX = RRuleWeekdayByIndex

const AbsenceLockSchema = z
  .object({
    name: z.string().min(1),
    lockActions: z.number().min(0).max(7),
    adminOverride: z.boolean(),
    permanent: z.boolean(),
    configRules: z
      .array(
        z.object({
          durationDays: z.number().min(1),
          frequency: z.enum(['WEEKLY', 'MONTHLY', 'YEARLY']),
          interval: z.number().min(1),
          startDate: z.date(),
          byWeekdays: z.number().int().min(0).max(127),
          startDay: z.number().min(-31).max(31),
          month: z.number().min(0).max(11),
        })
      )
      .min(1),
  })
  .superRefine((values, ctx) => {
    values.configRules.forEach((rule, idx) => {
      if (rule.frequency === 'WEEKLY' && rule.byWeekdays === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select at least one weekday.',
          path: ['configRules', idx, 'byWeekdays'],
        })
      }
    })
  })

export type AbsenceLockForm = z.infer<typeof AbsenceLockSchema>

interface Props {
  id: string | null
}

export function AbsenceLockEditInner({ id }: Props) {
  const translations = useTranslate({
    save: 'common.save',
    error: 'absences.absence-lock.form.error',
    successCreate: 'absences.absence-lock.form.success-create',
    successUpdate: 'absences.absence-lock.form.success-update',

    labels: {
      name: 'absences.absence-lock.form.labels.name',
      lockActions: 'absences.absence-lock-dialog.lock-actions.label',
      lockActionsDescription:
        'absences.absence-lock-dialog.lock-actions.description',
      adminOverride: 'absences.absence-lock-dialog.admin-override.label',
      adminOverrideDescription:
        'absences.absence-lock-dialog.admin-override.description',
      permanent: 'absences.absence-lock-dialog.permanent.label',
      permanentDescription:
        'absences.absence-lock-dialog.permanent.description',
    },

    rules: {
      title: 'absences.absence-lock.form.rules.title',
      add: 'common.add',
      empty: 'absences.absence-lock.form.rules.empty',
      ruleLabel: 'absences.absence-lock.form.rules.rule-label',
      duration: 'common.duration',
      durationDescription:
        'absences.absence-lock.form.rules.duration-description',
      frequency: 'absences.absence-lock.form.rules.frequency',
      frequencyDescription:
        'absences.absence-lock.form.rules.frequency-description',
      interval: 'absences.absence-lock.form.rules.interval',
      intervalDescription:
        'absences.absence-lock.form.rules.interval-description',
      startDate: 'absences.absence-lock.form.rules.start-date',
      startDateDescription:
        'absences.absence-lock.form.rules.start-date-description',
      remove: 'common.remove',
      weekdays: 'absences.absence-lock.form.rules.weekdays',
      weekdaysDescription:
        'absences.absence-lock.form.rules.weekdays-description',
      startDayOfMonth: 'absences.absence-lock.form.rules.start-day-of-month',
      startDayOfMonthDescription:
        'absences.absence-lock.form.rules.start-day-of-month-description',
      month: 'common.month',
      monthDescription: 'absences.absence-lock.form.rules.month-description',
      weekly: 'common.weekly',
      monthly: 'common.monthly',
      yearly: 'absences.absence-lock.form.rules.yearly',
    },
  })

  const navigate = useNavigate()
  const addToast = useToast()

  const [error, setError] = useState(false)

  const { data: absenceLockConfigData } = useSuspenseQuery(
    ABSENCE_LOCK_CONFIG_QUERY,
    !id
      ? skipToken
      : {
          variables: { id },
        }
  )

  const config = absenceLockConfigData?.absenceLockConfig ?? null

  const [upsertConfig, { loading: upsertConfigLoading }] = useMutation(
    UPSERT_ABSENCE_LOCK_CONFIG_MUTATION,
    {
      refetchQueries: [
        'AbsenceLockConfigs',
        'AbsenceLockConfig',
        'ActiveAbsenceLock',
      ],
      awaitRefetchQueries: true,
    }
  )

  const form = useForm<AbsenceLockForm>({
    resolver: zodResolver(AbsenceLockSchema),
    defaultValues: {
      name: config?.name ?? '',
      lockActions: config?.lockActions ?? 6,
      adminOverride: config?.adminOverride ?? false,
      permanent: config?.permanent ?? false,
      configRules:
        config?.rules
          .filter(rule => rule.enabled && rule.ruleType === 'RECURRING')
          .map(rule => {
            const startDate = parseISO(rule.startDate)
            const parsed = parseRRuleParts(rule.rrule)
            const rawFrequency = parsed.frequency ?? 'MONTHLY'
            const frequency =
              rawFrequency === 'DAILY'
                ? 'WEEKLY'
                : rawFrequency === 'WEEKLY' ||
                  rawFrequency === 'MONTHLY' ||
                  rawFrequency === 'YEARLY'
                ? rawFrequency
                : 'MONTHLY'

            const fallbackWeekday = WEEKDAY_BY_INDEX[startDate.getDay()] ?? 'MO'
            const byWeekdays =
              parsed.byWeekdays.length > 0
                ? weekdaysToBitmask(parsed.byWeekdays)
                : frequency === 'WEEKLY'
                ? weekdaysToBitmask([fallbackWeekday])
                : 0

            const startDay =
              parsed.byMonthDays[0] ??
              (frequency === 'WEEKLY' ? 1 : startDate.getDate())

            const monthValue = parsed.byMonths[0]
            const month =
              typeof monthValue === 'number'
                ? Math.max(0, Math.min(11, monthValue - 1))
                : startDate.getMonth()

            return {
              durationDays: rule.durationDays,
              frequency,
              interval: parsed.interval,
              startDate,
              byWeekdays,
              startDay: startDay === 0 ? 1 : startDay,
              month,
            }
          }) ?? [],
    },
    mode: 'onSubmit',
  })

  const rules = useFormWatch(form, 'configRules')

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'configRules',
  })

  const frequencyOptions = [
    {
      value: 'WEEKLY',
      label: translations.rules.weekly,
    },
    {
      value: 'MONTHLY',
      label: translations.rules.monthly,
    },
    {
      value: 'YEARLY',
      label: translations.rules.yearly,
    },
  ]

  function handleCreateRule() {
    append({
      durationDays: 1,
      frequency: 'MONTHLY',
      interval: 1,
      startDate: new Date(),
      byWeekdays: 0,
      startDay: 1,
      month: new Date().getMonth(),
    })
  }

  function handleSubmitInvalid(errors: FieldErrors<AbsenceLockForm>) {
    setError(true)
    console.warn('Form errors:', errors)
  }

  async function handleSubmit(values: AbsenceLockForm) {
    setError(false)

    const rules = values.configRules.map(rule => {
      const isWeekly = rule.frequency === 'WEEKLY'
      const isYearly = rule.frequency === 'YEARLY'

      const rrule = buildRRule({
        frequency: rule.frequency,
        interval: rule.interval,
        byWeekdays: isWeekly ? bitmaskToWeekdays(rule.byWeekdays) : undefined,
        byMonths: isYearly ? [rule.month + 1] : undefined,
        byMonthDays: !isWeekly ? [rule.startDay] : undefined,
      })

      return {
        enabled: true,
        ruleType: 'RECURRING' as const,
        startDate: dateFormat(rule.startDate),
        durationDays: rule.durationDays,
        rrule,
      }
    })

    const variables: VariablesOf<typeof UPSERT_ABSENCE_LOCK_CONFIG_MUTATION> = {
      id: id ?? null,
      name: values.name,
      enabled: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      lockActions: values.lockActions,
      adminOverride: values.adminOverride,
      permanent: values.permanent,
      rules,
    }

    const created = await upsertConfig({ variables })

    if (!id && !!created?.data) {
      addToast('success', translations.successCreate)
      await navigate(
        `/absences/edit-lock/${created.data.upsertAbsenceLockConfig.absenceLockConfig.id}`
      )
    } else if (!!id) {
      addToast('success', translations.successUpdate)
    }
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit, handleSubmitInvalid)}>
      <FieldGroup className="grid md:grid-cols-4 gap-4">
        <Controller
          control={form.control}
          name="name"
          render={({ field: { value, onChange } }) => (
            <Field>
              <FieldLabel>{translations.labels.name}</FieldLabel>

              <FieldContent className="justify-start">
                <Input value={value} onChange={onChange} />
              </FieldContent>
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="lockActions"
          render={({ field: { value, onChange } }) => (
            <Field className="flex">
              <FieldLabel>{translations.labels.lockActions}</FieldLabel>

              <FieldDescription>
                {translations.labels.lockActionsDescription}
              </FieldDescription>

              <FieldContent className="justify-start">
                <LockActionsToggle
                  className="bg-background"
                  itemClassName="h-10 px-4"
                  value={value ?? 0}
                  onChange={onChange}
                />
              </FieldContent>
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="adminOverride"
          render={({ field: { value, onChange } }) => (
            <Field className="flex justify-start">
              <div className="flex items-start gap-2">
                <Checkbox
                  className="bg-background"
                  id="admin-override"
                  value={value}
                  onChange={onChange}
                />

                <div>
                  <FieldLabel htmlFor="admin-override">
                    {translations.labels.adminOverride}
                  </FieldLabel>

                  <FieldDescription>
                    {translations.labels.adminOverrideDescription}
                  </FieldDescription>
                </div>
              </div>
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="permanent"
          render={({ field: { value, onChange } }) => (
            <Field className="flex justify-start">
              <div className="flex items-start gap-2">
                <Checkbox
                  className="bg-background"
                  id="permanent"
                  value={value}
                  onChange={onChange}
                />

                <div>
                  <FieldLabel htmlFor="permanent">
                    {translations.labels.permanent}
                  </FieldLabel>

                  <FieldDescription>
                    {translations.labels.permanentDescription}
                  </FieldDescription>
                </div>
              </div>
            </Field>
          )}
        />
      </FieldGroup>

      <div className="mt-8 rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{translations.rules.title}</h3>

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCreateRule}
          >
            {translations.rules.add}
          </Button>
        </div>

        {!fields.length ? (
          <p className="text-sm text-muted-foreground mt-2">
            {translations.rules.empty}
          </p>
        ) : (
          <div className="mt-4 flex flex-col gap-4">
            {fields.map((field, idx) => {
              const frequency = rules[idx]?.frequency ?? 'MONTHLY'
              const isWeekly = frequency === 'WEEKLY'
              const isMonthly = frequency === 'MONTHLY'
              const isYearly = frequency === 'YEARLY'
              const interval = rules[idx]?.interval ?? 1
              const showStartDate = interval > 1

              const upperGridCols = showStartDate
                ? 'md:grid-cols-3'
                : 'md:grid-cols-2'
              const lowerGridCols = isYearly
                ? 'md:grid-cols-3'
                : isMonthly
                ? 'md:grid-cols-2'
                : 'md:grid-cols-1'

              return (
                <div
                  key={field.id}
                  className="rounded-md border border-border/60 bg-muted/30 p-4"
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold">
                      {translations.rules.ruleLabel} {idx + 1}
                    </h4>

                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(idx)}
                    >
                      {translations.rules.remove}
                    </Button>
                  </div>

                  <div className={`mt-4 grid gap-4 ${upperGridCols}`}>
                    <Controller
                      name={`configRules.${idx}.frequency`}
                      control={form.control}
                      render={({ field: { value, onChange } }) => (
                        <Field>
                          <FieldLabel>
                            {translations.rules.frequency}
                          </FieldLabel>

                          <FieldDescription>
                            {translations.rules.frequencyDescription}
                          </FieldDescription>

                          <FieldContent className="justify-end">
                            <Combobox
                              className="bg-background"
                              curtainClassName="bg-background"
                              value={value}
                              items={frequencyOptions}
                              mode="single"
                              noSearch
                              onChange={value => {
                                if (!value) return
                                onChange(value)

                                if (value === 'WEEKLY')
                                  form.setValue(
                                    `configRules.${idx}.durationDays`,
                                    1,
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    }
                                  )
                              }}
                            />
                          </FieldContent>
                        </Field>
                      )}
                    />

                    <Controller
                      name={`configRules.${idx}.interval`}
                      control={form.control}
                      render={({ field: { value, onChange } }) => (
                        <Field>
                          <FieldLabel>{translations.rules.interval}</FieldLabel>

                          <FieldDescription>
                            {translations.rules.intervalDescription}
                          </FieldDescription>

                          <FieldContent className="justify-end">
                            <NumberInput
                              value={value ?? 1}
                              min={1}
                              suffix="x"
                              onChange={newValue => {
                                const value = newValue ?? 1
                                onChange(value)
                                if (value === 1)
                                  form.setValue(
                                    `configRules.${idx}.startDate`,
                                    new Date(),
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    }
                                  )
                              }}
                            />
                          </FieldContent>
                        </Field>
                      )}
                    />

                    <Activity mode={showStartDate ? 'visible' : 'hidden'}>
                      <Controller
                        name={`configRules.${idx}.startDate`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <Field>
                            <FieldLabel>
                              {translations.rules.startDate}
                            </FieldLabel>

                            <FieldDescription>
                              {translations.rules.startDateDescription}
                            </FieldDescription>

                            <FieldContent className="justify-end">
                              <DatePicker
                                triggerClassName="bg-background"
                                value={value}
                                mode="single"
                                onChange={onChange}
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </Activity>
                  </div>

                  <div className={`mt-4 grid gap-4 ${lowerGridCols}`}>
                    <Activity mode={isWeekly ? 'visible' : 'hidden'}>
                      <Controller
                        name={`configRules.${idx}.byWeekdays`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <Field>
                            <FieldLabel>
                              {translations.rules.weekdays}
                            </FieldLabel>

                            <FieldDescription>
                              {translations.rules.weekdaysDescription}
                            </FieldDescription>

                            <FieldContent className="justify-end">
                              <WeekdayToggleGroup
                                className="bg-background"
                                value={value ?? 0}
                                onChange={onChange}
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </Activity>

                    <Activity mode={isYearly ? 'visible' : 'hidden'}>
                      <Controller
                        name={`configRules.${idx}.month`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <Field>
                            <FieldLabel>{translations.rules.month}</FieldLabel>

                            <FieldDescription>
                              {translations.rules.monthDescription}
                            </FieldDescription>

                            <FieldContent className="justify-end">
                              <MonthCombobox
                                className="bg-background w-full!"
                                curtainClassName="bg-background"
                                value={value}
                                onChange={date => onChange(date)}
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </Activity>

                    <Activity mode={!isWeekly ? 'visible' : 'hidden'}>
                      <Controller
                        name={`configRules.${idx}.startDay`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <Field>
                            <FieldLabel>
                              {translations.rules.startDayOfMonth}
                            </FieldLabel>

                            <FieldDescription>
                              {translations.rules.startDayOfMonthDescription}
                            </FieldDescription>

                            <FieldContent className="justify-end">
                              <NumberInput
                                value={value}
                                min={-31}
                                max={31}
                                onChange={newValue =>
                                  typeof newValue === 'number' &&
                                  onChange(newValue)
                                }
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </Activity>

                    <Activity mode={!isWeekly ? 'visible' : 'hidden'}>
                      <Controller
                        name={`configRules.${idx}.durationDays`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <Field>
                            <FieldLabel>
                              {translations.rules.duration}
                            </FieldLabel>

                            <FieldDescription>
                              {translations.rules.durationDescription}
                            </FieldDescription>

                            <FieldContent className="justify-end">
                              <NumberInput
                                value={value ?? 1}
                                min={1}
                                suffix=" days"
                                onChange={newValue => onChange(newValue ?? 1)}
                              />
                            </FieldContent>
                          </Field>
                        )}
                      />
                    </Activity>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <AbsenceLockUpcoming rules={rules ?? []} />

      <div className="flex items-center justify-end gap-4 mt-4">
        {!!error && <div className="text-red-600">{translations.error}</div>}

        <Button
          type="submit"
          disabled={
            upsertConfigLoading ||
            !form.formState.isValid ||
            form.formState.isSubmitting
          }
        >
          {translations.save}
        </Button>
      </div>
    </form>
  )
}

export function AbsenceLockEdit() {
  const translations = useTranslate({
    headerCreate: 'absences.absence-lock.form.header-create',
    headerEdit: 'absences.absence-lock.form.header-edit',
  })

  const id = useParam('id', true) ?? null

  useBreadcrumbOverrides(
    {
      'edit-lock': !!id ? translations.headerEdit : translations.headerCreate,
      ...(!!id ? { [id]: null } : {}),
    },
    ['edit-lock']
  )

  return (
    <ModuleWrapper
      title={!!id ? translations.headerEdit : translations.headerCreate}
    >
      <Suspense
        fallback={
          <div className="flex items-center justify-center mt-8">
            <Loader.Spinner />
          </div>
        }
      >
        <AbsenceLockEditInner id={id} />
      </Suspense>
    </ModuleWrapper>
  )
}
